// src/components/AdminPortal.js
//
// Admin-only standalone portal at /admin.
//
// Flow:
//   - Not signed in              → show admin sign-in (Google + magic link)
//   - Signed in + admin email    → render AdminPage
//   - Signed in + not admin      → "Admin only" reject screen + sign-out
//   - AuthContext loading        → loading screen
//
// Mounted outside EpisodeBuilderApp via App.js isStandalonePath('/admin'),
// so the main-app shell never loads on this route — keeps the surface
// minimal and avoids any non-admin user seeing main-app UI even briefly.

import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import { signInWithProvider, signOut, supabase } from '../lib/supabase';
import { authRedirectBase } from '../lib/hosts';
import { isHardcodedAdminEmail } from '../lib/memory';
import AdminPage from '../pages/AdminPage';
import Spinner from './shared/Spinner';

// Component-scoped styles. Self-contained so the admin portal doesn't
// pull in any landing-v2 / main-app CSS surface and can't be styled
// out from under itself by global rules. Dark theme, brand-tinted
// gradient button.
const PORTAL_STYLES = `
.admin-portal-shell {
  position: fixed; inset: 0;
  display: flex; align-items: center; justify-content: center;
  background-color: #050505;
  background-image: url('/episode_landing_bg.jpg');
  background-size: cover;
  background-position: center top;
  color: #f5ede0;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  padding: 24px;
  z-index: 1;
}
.admin-portal-shell::before {
  content: ''; position: absolute; inset: 0;
  background: linear-gradient(180deg, rgba(5,5,5,0.5), rgba(5,5,5,0.85));
  z-index: 0;
}
.admin-portal-card {
  position: relative; z-index: 1;
  width: 100%; max-width: 420px;
  padding: 36px 32px 28px;
  background: rgba(20, 14, 28, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 22px;
  backdrop-filter: blur(28px);
  -webkit-backdrop-filter: blur(28px);
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.55);
}
.admin-portal-brand {
  font-size: 12px; letter-spacing: 0.18em; text-transform: uppercase;
  font-weight: 600; color: rgba(255, 217, 168, 0.85);
  margin-bottom: 18px;
}
.admin-portal-title {
  margin: 0 0 8px;
  font-family: 'Playfair Display', serif;
  font-size: 28px; font-weight: 600; letter-spacing: -0.02em;
  color: #ffffff;
}
.admin-portal-sub {
  margin: 0 0 24px;
  font-size: 14px; line-height: 1.55;
  color: rgba(255, 255, 255, 0.65);
}
.admin-portal-fineprint {
  margin: 14px 0 0;
  font-size: 12px; line-height: 1.5;
  color: rgba(255, 255, 255, 0.45);
}
.admin-portal-loading {
  text-align: center; padding: 16px 0;
  color: rgba(255, 255, 255, 0.6);
}
.admin-portal-actions {
  display: flex; flex-direction: column; gap: 14px;
}
.admin-portal-btn {
  border: 0;
  border-radius: 12px;
  padding: 13px 18px;
  font: inherit; font-size: 14px; font-weight: 600;
  cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; gap: 10px;
  transition: transform 0.12s, filter 0.18s;
}
.admin-portal-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.admin-portal-btn--primary {
  background: linear-gradient(135deg, #4c1d95 0%, #0a648e 55%, #ef506a 100%);
  color: #fff;
  box-shadow: 0 8px 24px rgba(76, 29, 149, 0.4);
}
.admin-portal-btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  filter: brightness(1.08);
}
.admin-portal-btn--google {
  background: rgba(255, 255, 255, 0.96);
  color: #1f1f1f;
}
.admin-portal-btn--google:hover:not(:disabled) {
  transform: translateY(-1px);
  background: #ffffff;
}
.admin-portal-divider {
  display: flex; align-items: center; gap: 12px;
  margin: 18px 0;
  color: rgba(255, 255, 255, 0.35);
  font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase;
}
.admin-portal-divider::before, .admin-portal-divider::after {
  content: ''; flex: 1; height: 1px;
  background: rgba(255, 255, 255, 0.1);
}
.admin-portal-form {
  display: flex; flex-direction: column; gap: 14px;
}
.admin-portal-label {
  display: flex; flex-direction: column; gap: 6px;
  font-size: 12px; letter-spacing: 0.02em;
  color: rgba(255, 255, 255, 0.6);
}
.admin-portal-input {
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  padding: 12px 14px;
  color: #ffffff;
  font: inherit; font-size: 15px;
  outline: none;
  transition: border-color 0.18s, box-shadow 0.18s;
}
.admin-portal-input::placeholder { color: rgba(255, 255, 255, 0.35); }
.admin-portal-input:focus {
  border-color: rgba(124, 58, 237, 0.6);
  box-shadow: 0 0 0 3px rgba(76, 29, 149, 0.2);
}
.admin-portal-error {
  background: rgba(204, 51, 0, 0.1);
  border: 1px solid rgba(204, 51, 0, 0.3);
  border-radius: 10px;
  padding: 10px 13px;
  color: #ff8a6a; font-size: 13px; line-height: 1.45;
}
.admin-portal-back {
  margin-top: 22px;
  text-align: center;
}
.admin-portal-link {
  color: rgba(255, 255, 255, 0.45);
  font-size: 13px;
  text-decoration: none;
  transition: color 0.15s;
}
.admin-portal-link:hover {
  color: rgba(255, 255, 255, 0.85);
}
`;

export default function AdminPortal() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="admin-portal-shell">
        <style>{PORTAL_STYLES}</style>
        <div className="admin-portal-card">
          <div className="admin-portal-loading"><Spinner center /></div>
        </div>
      </div>
    );
  }

  // Signed in + admin email → render the actual admin dashboard.
  if (user && isHardcodedAdminEmail(user.email)) {
    return <AdminPage />;
  }

  // Signed in but NOT an admin email → blocked. Offer sign-out so the
  // user can switch accounts without leaving /admin.
  if (user && !isHardcodedAdminEmail(user.email)) {
    return (
      <div className="admin-portal-shell">
        <style>{PORTAL_STYLES}</style>
        <div className="admin-portal-card">
          <div className="admin-portal-brand">Episode · Admin</div>
          <h1 className="admin-portal-title">Admin only.</h1>
          <p className="admin-portal-sub">
            This area is restricted to whitelisted admin accounts. You're signed in as <strong>{user.email}</strong>.
          </p>
          <div className="admin-portal-actions">
            <button
              type="button"
              className="admin-portal-btn admin-portal-btn--primary"
              onClick={async () => { await signOut(); }}
            >
              Sign out
            </button>
            <a className="admin-portal-link" href="/">← Back to meetepio.com</a>
          </div>
        </div>
      </div>
    );
  }

  // Not signed in → admin sign-in form.
  return <AdminSignin />;
}

function AdminSignin() {
  const [email, setEmail]       = useState('');
  const [busy, setBusy]         = useState('');     // '' | 'google' | 'magic'
  const [error, setError]       = useState('');
  const [linkSent, setLinkSent] = useState(false);

  async function handleGoogle() {
    setError('');
    setBusy('google');
    try {
      await signInWithProvider('google');
      // Redirects offsite, then back to /admin (we send no returnTo so
      // the redirect lands at origin/, but the magic-link path below
      // explicitly tells Supabase to land at /admin).
    } catch (e) {
      setBusy('');
      setError(e?.message || 'Google sign-in failed.');
    }
  }

  async function handleMagicLink(e) {
    e.preventDefault();
    setError('');
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) { setError('Email required.'); return; }
    if (!supabase)   { setError('Auth not configured.'); return; }
    setBusy('magic');
    try {
      const redirectTo = `${authRedirectBase()}/admin`;
      const { error: err } = await supabase.auth.signInWithOtp({
        email: cleanEmail,
        options: {
          shouldCreateUser: false,            // admin-only — don't auto-create
          emailRedirectTo:  redirectTo,
        },
      });
      if (err) throw err;
      setLinkSent(true);
    } catch (err) {
      setError(err?.message || 'Could not send sign-in link.');
    } finally {
      setBusy('');
    }
  }

  const isBusy = !!busy;

  return (
    <div className="admin-portal-shell">
      <style>{PORTAL_STYLES}</style>
      <div className="admin-portal-card">
        <div className="admin-portal-brand">Episode · Admin</div>

        {linkSent ? (
          <>
            <h1 className="admin-portal-title">Check your inbox.</h1>
            <p className="admin-portal-sub">
              A sign-in link was sent to <strong>{email}</strong>. Click it to land back here.
            </p>
            <p className="admin-portal-fineprint">
              The link won't work for non-admin accounts — anyone else will hit the "Admin only" reject screen.
            </p>
          </>
        ) : (
          <>
            <h1 className="admin-portal-title">Sign in.</h1>
            <p className="admin-portal-sub">Restricted to whitelisted admin accounts.</p>

            <button
              type="button"
              className="admin-portal-btn admin-portal-btn--google"
              onClick={handleGoogle}
              disabled={isBusy}
            >
              <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden style={{ flex: '0 0 auto' }}>
                <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.2l6.7-6.7C35.6 2.5 30.2 0 24 0 14.8 0 6.9 5.3 3 13l7.8 6.1C12.6 13.3 17.8 9.5 24 9.5z"/>
                <path fill="#34A853" d="M46.8 24.5c0-1.6-.1-3.2-.4-4.7H24v9h12.8c-.6 3.1-2.4 5.7-5.1 7.5l7.8 6.1c4.6-4.2 7.3-10.4 7.3-17.9z"/>
                <path fill="#FBBC05" d="M10.8 28.1A14.5 14.5 0 0 1 10 24c0-1.4.2-2.8.7-4.1L3 13.9A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.7l8.2-6.6z"/>
                <path fill="#4285F4" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.8-6.1c-2.2 1.5-5 2.4-8.1 2.4-6.2 0-11.4-3.8-13.3-9.1l-7.9 6.1A24 24 0 0 0 24 48z"/>
              </svg>
              <span>{busy === 'google' ? 'Connecting…' : 'Continue with Google'}</span>
            </button>

            <div className="admin-portal-divider"><span>or</span></div>

            <form className="admin-portal-form" onSubmit={handleMagicLink}>
              <label className="admin-portal-label">
                <span>Admin email</span>
                <input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="admin-portal-input"
                  required
                  disabled={isBusy}
                />
              </label>

              {error && <div className="admin-portal-error">{error}</div>}

              <button
                type="submit"
                className="admin-portal-btn admin-portal-btn--primary"
                disabled={isBusy || !email.trim()}
              >
                {busy === 'magic' ? 'Sending…' : 'Send magic link'}
              </button>
            </form>
          </>
        )}

        <div className="admin-portal-back">
          <a href="/" className="admin-portal-link">← meetepio.com</a>
        </div>
      </div>
    </div>
  );
}
